import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, isLeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import LineItems from "@/components/LineItems";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const L = ({ children }) => (
  <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{children}</Label>
);

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const blank = () => ({
  title: "", date: todayLocal(), customer_id: "", customer_name: "", address: "",
  work_hours: "", work_minutes: "", travel_hours: "", travel_minutes: "",
  note: "", ready_to_invoice: false, assigned_to: [], materials: [],
});

export const QuickJobDialog = ({ open, onOpenChange, staff = [], onSaved }) => {
  const { user } = useAuth();
  const leader = isLeader(user?.role);
  const [form, setForm] = useState(blank());
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(blank());
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      customer_id: id,
      customer_name: c?.name || f.customer_name,
      address: f.address || c?.address || "",
    }));
  };

  const toggleTech = (id) =>
    setForm((f) => ({
      ...f,
      assigned_to: f.assigned_to.includes(id)
        ? f.assigned_to.filter((x) => x !== id)
        : [...f.assigned_to, id],
    }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Rubrik krävs");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/jobs/manual", {
        title: form.title.trim(),
        date: form.date,
        customer_id: form.customer_id || null,
        customer_name: form.customer_name || null,
        address: form.address || null,
        work_hours: parseInt(form.work_hours, 10) || 0,
        work_minutes: parseInt(form.work_minutes, 10) || 0,
        travel_hours: parseInt(form.travel_hours, 10) || 0,
        travel_minutes: parseInt(form.travel_minutes, 10) || 0,
        note: form.note || null,
        materials: form.materials,
        ready_to_invoice: form.ready_to_invoice,
        assigned_to: leader && form.assigned_to.length > 0 ? form.assigned_to : null,
      });
      toast.success(`Arbetsorder #${String(data.order_number).padStart(4, "0")} skapad`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-3xl" data-testid="quick-job-dialog">
        <DialogHeader>
          <DialogTitle>Ny arbetsorder</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <L>Rubrik</L>
            <Input
              data-testid="quick-job-title-input"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              className="mt-1 h-10"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <L>Datum</L>
              <Input
                type="date"
                data-testid="quick-job-date-input"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <L>Kund i systemet</L>
              <Select value={form.customer_id} onValueChange={pickCustomer}>
                <SelectTrigger data-testid="quick-job-customer-select" className="mt-1 h-10">
                  <SelectValue placeholder="Välj kund (frivilligt)" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`quick-job-customer-option-${c.id}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <L>Kund (fritext)</L>
              <Input
                data-testid="quick-job-customer-name-input"
                value={form.customer_name}
                onChange={(e) => set("customer_name", e.target.value)}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <L>Adress</L>
              <Input
                data-testid="quick-job-address-input"
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                className="mt-1 h-10"
              />
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-semibold text-slate-800">Tid (skrivs in manuellt)</p>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <L>Arbetstid tim</L>
                <Input
                  data-testid="quick-job-work-hours-input"
                  inputMode="numeric"
                  placeholder="2"
                  value={form.work_hours}
                  onChange={(e) => set("work_hours", e.target.value)}
                  className="mt-1 h-10 bg-white"
                />
              </div>
              <div>
                <L>Arbetstid min</L>
                <Input
                  data-testid="quick-job-work-minutes-input"
                  inputMode="numeric"
                  placeholder="30"
                  value={form.work_minutes}
                  onChange={(e) => set("work_minutes", e.target.value)}
                  className="mt-1 h-10 bg-white"
                />
              </div>
              <div>
                <L>Restid tim</L>
                <Input
                  data-testid="quick-job-travel-hours-input"
                  inputMode="numeric"
                  placeholder="0"
                  value={form.travel_hours}
                  onChange={(e) => set("travel_hours", e.target.value)}
                  className="mt-1 h-10 bg-white"
                />
              </div>
              <div>
                <L>Restid min</L>
                <Input
                  data-testid="quick-job-travel-minutes-input"
                  inputMode="numeric"
                  placeholder="45"
                  value={form.travel_minutes}
                  onChange={(e) => set("travel_minutes", e.target.value)}
                  className="mt-1 h-10 bg-white"
                />
              </div>
            </div>
          </div>

          <div>
            <L>Fritext</L>
            <Textarea
              data-testid="quick-job-note-input"
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>

          {leader && (
            <div>
              <L>Tilldela tekniker (frivilligt – annars du själv)</L>
              <div
                data-testid="quick-job-assignee-list"
                className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2"
              >
                {staff.map((s) => (
                  <label
                    key={s.id}
                    htmlFor={`quick-tech-${s.id}`}
                    className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-2 transition-colors duration-200 hover:bg-slate-50"
                  >
                    <Checkbox
                      id={`quick-tech-${s.id}`}
                      data-testid={`quick-job-assignee-option-${s.id}`}
                      checked={form.assigned_to.includes(s.id)}
                      onCheckedChange={() => toggleTech(s.id)}
                    />
                    <span className="truncate text-sm font-medium text-slate-800">{s.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <LineItems
            label="Material"
            testid="quick-job-materials"
            category="material"
            items={form.materials}
            onChange={(v) => set("materials", v)}
          />

          <label
            htmlFor="quick-ready"
            className="flex cursor-pointer items-center gap-2.5 rounded-md border border-slate-200 p-3"
          >
            <Checkbox
              id="quick-ready"
              data-testid="quick-job-ready-checkbox"
              checked={form.ready_to_invoice}
              onCheckedChange={(v) => set("ready_to_invoice", !!v)}
            />
            <span className="text-sm font-medium text-slate-800">
              Färdig för fakturering – ordern hamnar direkt under fliken Avslutat
            </span>
          </label>

          <Button
            data-testid="quick-job-save-button"
            disabled={busy}
            onClick={save}
            className="h-11 w-full bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Spara arbetsorder</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
